import { Component, OnInit , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { PersistColumnWidthsDirective } from '../../shared/directives/persist-column-widths.directive';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { Checkbox } from 'primeng/checkbox';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { AllocationService, CreateAllocationDto, CopyRequestDto, EmployeeMonthlyAllocationSummary, AllocationExportRequest } from '../../services/allocation.service';
import { EmployeeService } from '../../services/employee.service';
import { ProjectService } from '../../services/project.service';
import { CostCenterService } from '../../services/cost-center.service';
import { RoleService } from '../../services/role.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { AuthService } from '../../services/auth.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { AllocationPredictorService, AvailablePercentageInfo } from '../../services/allocation-predictor.service';
import { Allocation, InvoiceType, Month } from '../../models/allocation.model';
import { Employee } from '../../models/employee.model';
import { Project } from '../../models/project.model';
import { CostCenter } from '../../models/cost-center.model';
import { Role } from '../../models/role.model';
import { ServiceGroup } from '../../models/service-group.model';
import { saveAs } from 'file-saver';
import { environment } from '../../../environments/environment';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { autoFitColumns, addAutoFilter } from '../../utils/excel-export.util';

interface DropdownOption {
  label: string;
  value: number | string;
}

/** Ticked state of the Add/Edit form's "Show" checkboxes, one per kind of allocatable row. */
interface FormProjectTypeFilter {
  main: boolean;
  presales: boolean;
  activities: boolean;
}

/** One row in the allocation table: project allocation (first row may include employee info), synthetic available row, or total. */
export type AllocationDisplayRow =
  | { rowType: 'project'; allocation: Allocation; isFirstInGroup: boolean; employeeDisplay?: { employeeName: string; initials: string; roleLabel: string }; sortEmployeeKey: string; sortProjectName: string; sortInvoiceType: number }
  | { rowType: 'available'; employeeName: string; availableWeeks: number[]; sortEmployeeKey: string; sortProjectName: string; sortInvoiceType: number }
  | { rowType: 'total'; employeeName: string; totals: number[]; sortEmployeeKey: string; sortProjectName: string; sortInvoiceType: number };

@Component({
  selector: 'app-allocation-grid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputNumber,
    TableModule,
    PersistColumnWidthsDirective,
    Button,
    Dialog,
    ConfirmDialog,
    Select,
    MultiSelect,
    InputText,
    Tooltip,
    Checkbox
  ],
  templateUrl: './allocation-grid.component.html',
  styleUrl: './allocation-grid.component.scss'
})
export class AllocationGridComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  allocations: Allocation[] = [];
  filteredAllocations: Allocation[] = [];
  monthlySummaries: EmployeeMonthlyAllocationSummary[] = [];
  employees: Employee[] = [];
  projects: Project[] = [];
  costCenters: CostCenter[] = [];
  roles: Role[] = [];
  serviceGroups: ServiceGroup[] = [];

  loading: boolean = true;
  displayDialog: boolean = false;
  displayUnallocatedDialog: boolean = false;
  loadingUnallocated: boolean = false;
  unallocatedEmployees: Employee[] = [];
  /** Active staff deliberately kept out of allocation — a policy choice, not a gap to fill. */
  excludedEmployees: Employee[] = [];
  /** Leavers whose last working day falls on or before the selected month. */
  inactiveEmployees: Employee[] = [];
  workforceTab: 'unallocated' | 'excluded' | 'inactive' = 'unallocated';
  isEditMode: boolean = false;
  searchText: string = '';
  searchSuggestions: any[] = [];

  // Filter properties
  selectedRoles: string[] = [];
  selectedPMs: string[] = [];
  selectedProjects: string[] = [];
  selectedInvoiceTypes: number[] = [];
  selectedCostCenters: string[] = [];
  selectedServiceGroups: string[] = [];
  selectedStatusFilter: 'fully' | 'over' | 'partial' | null = null;
  /** When true, only show employees who have any week with available capacity (< 100%). */
  showOnlyAvailable: boolean = false;
  /**
   * Narrows the grid to in-pursuit presales work or committed delivery work.
   * Presales deals are allocated against a twin Projects row flagged IsPresales, which
   * reaches the client as AllocationDto.IsProjectPresales — so this is a plain client-side
   * predicate, not a separate data source. 'all' keeps the historical behaviour.
   */
  projectTypeFilter: 'all' | 'presales' | 'presales-projects' | 'presales-activities' | 'main' = 'all';

  // Filter options
  pmOptions: any[] = [];
  projectOptions: any[] = [];
  costCenterOptions: any[] = [];
  serviceGroupOptions: any[] = [];
  // 'presales' keeps both kinds together, since "show me the pursuit work" is still the
  // common ask; the two below split it when someone needs deals or activities on their own.
  projectTypeOptions: any[] = [
    { label: 'All projects', value: 'all' },
    { label: 'Presales projects & activities', value: 'presales' },
    { label: '— Presales projects only', value: 'presales-projects' },
    { label: '— Presales activities only', value: 'presales-activities' },
    { label: 'Main projects', value: 'main' }
  ];

  selectedAllocation: Allocation = this.getEmptyAllocation();
  fieldTouched: Record<string, boolean> = {};

  // Year and Month selection
  selectedYear: number;
  selectedMonth: Month;
  isMonthClosed: boolean = false;

  yearOptions: DropdownOption[] = [];
  monthOptions: DropdownOption[] = [
    { label: 'January', value: Month.January },
    { label: 'February', value: Month.February },
    { label: 'March', value: Month.March },
    { label: 'April', value: Month.April },
    { label: 'May', value: Month.May },
    { label: 'June', value: Month.June },
    { label: 'July', value: Month.July },
    { label: 'August', value: Month.August },
    { label: 'September', value: Month.September },
    { label: 'October', value: Month.October },
    { label: 'November', value: Month.November },
    { label: 'December', value: Month.December }
  ];

  roleOptions: DropdownOption[] = [];

  /**
   * Everyone who should appear on an allocation plan: active and not excluded. Deliberately
   * NOT filtered by month — this is the denominator the page is built around, and the point
   * of the screen is to surface the people who have no allocation yet. Narrowing it to those
   * who already hold a row for the month would hide exactly the ones that need assigning.
   */
  get activeEmployeesForPeriod(): Employee[] {
    return this.employees.filter(e => e.isActive === true && e.excludeFromAllocation !== true);
  }

  /** Total employees count for stats card. */
  get totalEmployeesCount(): number {
    return this.activeEmployeesForPeriod.length;
  }

  /** Per-employee week totals (employeeId -> [w1, w2, w3, w4, w5]) for current period. */
  private getEmployeeWeekTotals(): Map<string, number[]> {
    const map = new Map<string, number[]>();
    const periodAllocations = this.allocations.filter(
      a => a.year === this.selectedYear && a.month === this.selectedMonth
    );
    for (const a of periodAllocations) {
      const id = a.employeeId;
      if (!map.has(id)) map.set(id, [0, 0, 0, 0, 0]);
      const arr = map.get(id)!;
      arr[0] += a.week1Percentage ?? 0;
      arr[1] += a.week2Percentage ?? 0;
      arr[2] += a.week3Percentage ?? 0;
      arr[3] += a.week4Percentage ?? 0;
      arr[4] += a.week5Percentage ?? 0;
    }
    return map;
  }

  /** Fully allocated count (every week total = 100). */
  get fullyAllocatedCount(): number {
    const totals = this.getEmployeeWeekTotals();
    let count = 0;
    for (const emp of this.activeEmployeesForPeriod) {
      const w = totals.get(emp.id) ?? [0, 0, 0, 0, 0];
      const all100 = w.every(v => Math.round(v) === 100);
      if (all100) count++;
    }
    return count;
  }

  /** Over allocated count (any week total > 100). */
  get overAllocatedCount(): number {
    const totals = this.getEmployeeWeekTotals();
    let count = 0;
    for (const emp of this.activeEmployeesForPeriod) {
      const w = totals.get(emp.id) ?? [0, 0, 0, 0, 0];
      if (w.some(v => Math.round(v) > 100)) count++;
    }
    return count;
  }

  /**
   * Partly allocated: has some planned time, but is neither full in every week nor over in
   * any. Someone at 50% all month fell into none of the other three cards, so the breakdown
   * did not add up to the total and those people were invisible in the summary.
   */
  get partiallyAllocatedCount(): number {
    const totals = this.getEmployeeWeekTotals();
    let count = 0;
    for (const emp of this.activeEmployeesForPeriod) {
      const w = totals.get(emp.id) ?? [0, 0, 0, 0, 0];
      const isFull = w.every(v => Math.round(v) === 100);
      const isOver = w.some(v => Math.round(v) > 100);
      const isNone = w.every(v => Math.round(v) === 0);
      if (!isFull && !isOver && !isNone) count++;
    }
    return count;
  }

  /** Not allocated count (all weeks 0 or no allocation). */
  get notAllocatedCount(): number {
    const totals = this.getEmployeeWeekTotals();
    let count = 0;
    for (const emp of this.activeEmployeesForPeriod) {
      const w = totals.get(emp.id) ?? [0, 0, 0, 0, 0];
      if (w.every(v => Math.round(v) === 0)) count++;
    }
    return count;
  }

  /** Percentage of workforce fully allocated (for stats card sublabel and progress). */
  get fullyAllocatedPercent(): number {
    const total = this.totalEmployeesCount;
    if (total === 0) return 0;
    return Math.round((this.fullyAllocatedCount / total) * 100);
  }

  /** Cached display rows for the table (stable reference so button clicks work). Updated in applyFilters(). */
  allocationDisplayRowsCache: AllocationDisplayRow[] = [];

  /** Rows for the allocation table: employee header, project rows, then total per employee. */
  get allocationDisplayRows(): AllocationDisplayRow[] {
    return this.allocationDisplayRowsCache;
  }

  private buildAllocationDisplayRows(): void {
    const rows: AllocationDisplayRow[] = [];
    const byEmployee = new Map<string, Allocation[]>();
    for (const a of this.filteredAllocations) {
      const id = a.employeeId;
      if (!byEmployee.has(id)) byEmployee.set(id, []);
      byEmployee.get(id)!.push(a);
    }

    // Full-month non-Available allocations per employee for true remaining-capacity calculation
    const fullMonthByEmployee = new Map<string, Allocation[]>();
    for (const a of this.allocations) {
      if (a.year !== this.selectedYear || a.month !== this.selectedMonth) continue;
      if ((a.projectName ?? '').toLowerCase() === 'available') continue;
      const id = a.employeeId;
      if (!fullMonthByEmployee.has(id)) fullMonthByEmployee.set(id, []);
      fullMonthByEmployee.get(id)!.push(a);
    }

    for (const [empId, allocations] of byEmployee) {
      // Separate manual "Available" entries from real project rows
      const realAllocations = allocations.filter(a => (a.projectName ?? '').toLowerCase() !== 'available');
      const first = realAllocations[0] ?? allocations[0];
      const employeeName = first.employeeName ?? '';
      const roleLabel = [first.roleTitle ?? this.getRoleName(first.roleId), first.costCenterName, first.serviceGroupName]
        .filter(Boolean)
        .join(' · ') || '—';
      const employeeDisplay = { employeeName, initials: this.getEmployeeInitials(employeeName), roleLabel };
      const sortEmployeeBase = employeeName || ' ';

      if (realAllocations.length === 0) {
        // Edge case: only manual "Available" entries - show them as-is without a synthetic row
        allocations.forEach((a, i) => {
          rows.push({
            rowType: 'project',
            allocation: a,
            isFirstInGroup: i === 0,
            employeeDisplay: i === 0 ? employeeDisplay : undefined,
            sortEmployeeKey: sortEmployeeBase + 'A',
            sortProjectName: a.projectName ?? '',
            sortInvoiceType: a.invoiceType ?? 0
          });
        });
        const edgeTotals = [0, 0, 0, 0, 0];
        for (const a of allocations) {
          edgeTotals[0] += a.week1Percentage ?? 0;
          edgeTotals[1] += a.week2Percentage ?? 0;
          edgeTotals[2] += a.week3Percentage ?? 0;
          edgeTotals[3] += a.week4Percentage ?? 0;
          edgeTotals[4] += a.week5Percentage ?? 0;
        }
        rows.push({ rowType: 'total', employeeName, totals: edgeTotals.map(t => Math.round(t)), sortEmployeeKey: sortEmployeeBase + 'Z', sortProjectName: '￿', sortInvoiceType: 999 });
        continue;
      }

      // Normal case: show real project rows (manual "Available" entries suppressed - replaced by synthetic row below)
      realAllocations.forEach((a, i) => {
        rows.push({
          rowType: 'project',
          allocation: a,
          isFirstInGroup: i === 0,
          employeeDisplay: i === 0 ? employeeDisplay : undefined,
          sortEmployeeKey: sortEmployeeBase + 'A',
          sortProjectName: a.projectName ?? '',
          sortInvoiceType: a.invoiceType ?? 0
        });
      });

      // Use DB summary if available; fall back to computing from allocation rows
      const dbSummary = this.monthlySummaries.find(s => s.employeeId === empId);
      let availableWeeks: number[];
      if (dbSummary) {
        availableWeeks = [
          Math.max(0, Math.round(dbSummary.availableWeek1)),
          Math.max(0, Math.round(dbSummary.availableWeek2)),
          Math.max(0, Math.round(dbSummary.availableWeek3)),
          Math.max(0, Math.round(dbSummary.availableWeek4)),
          Math.max(0, Math.round(dbSummary.availableWeek5))
        ];
      } else {
        const fullTotals = [0, 0, 0, 0, 0];
        for (const a of (fullMonthByEmployee.get(empId) ?? [])) {
          fullTotals[0] += a.week1Percentage ?? 0;
          fullTotals[1] += a.week2Percentage ?? 0;
          fullTotals[2] += a.week3Percentage ?? 0;
          fullTotals[3] += a.week4Percentage ?? 0;
          fullTotals[4] += a.week5Percentage ?? 0;
        }
        availableWeeks = fullTotals.map(t => Math.max(0, 100 - Math.round(t)));
      }
      if (availableWeeks.some(v => v > 0)) {
        rows.push({
          rowType: 'available',
          employeeName,
          availableWeeks,
          sortEmployeeKey: sortEmployeeBase + 'M',
          sortProjectName: '￾',
          sortInvoiceType: 998
        });
      }

      // Total row from real (non-Available) allocations visible in the filtered view
      const totals = [0, 0, 0, 0, 0];
      for (const a of realAllocations) {
        totals[0] += a.week1Percentage ?? 0;
        totals[1] += a.week2Percentage ?? 0;
        totals[2] += a.week3Percentage ?? 0;
        totals[3] += a.week4Percentage ?? 0;
        totals[4] += a.week5Percentage ?? 0;
      }
      rows.push({
        rowType: 'total',
        employeeName,
        totals: totals.map(t => Math.round(t)),
        sortEmployeeKey: sortEmployeeBase + 'Z',
        sortProjectName: '￿',
        sortInvoiceType: 999
      });
    }
    this.allocationDisplayRowsCache = rows;
  }

  /** Role options for add/edit allocation: only roles assigned to the selected employee, or all if none selected. */
  get allocationRoleOptions(): DropdownOption[] {
    const employeeId = this.selectedAllocation.employeeId;
    if (!employeeId) return this.roleOptions;
    const emp = this.employees.find(e => e.id === employeeId);
    if (!emp) return this.roleOptions;
    if (emp.roleIds && emp.roleIds.length > 0) {
      return this.roleOptions.filter(o => emp.roleIds!.includes(o.value as string));
    }
    return this.roleOptions;
  }

  invoiceTypeOptions: DropdownOption[] = [
    { label: 'Monthly', value: InvoiceType.Monthly },
    { label: 'Project Milestone', value: InvoiceType.ProjectMilestone },
    { label: 'N/A', value: InvoiceType.NotAvailable }
  ];

  // Standard allocation percentages
  allocationPercentageOptions: DropdownOption[] = [
    { label: '0%', value: 0 },
    { label: '5%', value: 5 },
    { label: '10%', value: 10 },
    { label: '15%', value: 15 },
    { label: '25%', value: 25 },
    { label: '50%', value: 50 },
    { label: '75%', value: 75 },
    { label: '100%', value: 100 }
  ];

  // Available percentage predictor properties
  availablePercentageInfo: AvailablePercentageInfo | null = null;
  showAvailablePercentageCard: boolean = false;
  weeklyRemainingPercentage: { [key: number]: number } = {};

  constructor(
    private allocationService: AllocationService,
    private employeeService: EmployeeService,
    private projectService: ProjectService,
    private costCenterService: CostCenterService,
    private roleService: RoleService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    private yearMonthFilterService: YearMonthFilterService,
    private allocationPredictorService: AllocationPredictorService,
    public pageHeaderService: PageHeaderSearchActionService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Sync year/month from shared filter (main header drives this on allocation page)
    this.selectedYear = this.yearMonthFilterService.currentYear;
    this.selectedMonth = this.yearMonthFilterService.currentMonth;

    // Handoff from the Team Allocation tab on the project / presales details dialogs:
    // /allocations?projectId=…&year=…&month=…&openForm=1. Read here (not ngOnInit) so the
    // requested month is in place before loadData() fetches that month's allocations.
    const qp = this.route.snapshot.queryParamMap;
    this.pendingProjectId = qp.get('projectId');
    this.pendingOpenForm = qp.get('openForm') === '1';

    // Year and month are validated independently — a bad or missing year must not silently
    // discard a good month, or the Add form would open against the wrong period.
    const qpYear = Number(qp.get('year'));
    if (Number.isInteger(qpYear) && qpYear >= 2000 && qpYear <= 2100) {
      this.selectedYear = qpYear;
      // Keep the shared header in step, or it would immediately push its own value back.
      this.yearMonthFilterService.setYear(qpYear);
    }

    const qpMonth = Number(qp.get('month'));
    if (Number.isInteger(qpMonth) && qpMonth >= 1 && qpMonth <= 12) {
      this.selectedMonth = qpMonth as Month;
      this.yearMonthFilterService.setMonth(qpMonth as Month);
    }

    // Populate year options (current year ± 2 years)
    const currentYear = new Date().getFullYear();
    for (let year = currentYear - 2; year <= currentYear + 2; year++) {
      this.yearOptions.push({ label: year.toString(), value: year });
    }
  }

  /** Set by the ?projectId=…&openForm=1 handoff; consumed once after the first load. */
  private pendingProjectId: string | null = null;
  private pendingOpenForm = false;

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    // When header year/month change, sync and reload
    this.yearMonthFilterService.year
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(y => {
        if (this.selectedYear !== y) {
          this.selectedYear = y;
          this.onYearMonthChange();
        }
      });
    this.yearMonthFilterService.month
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(m => {
        if (this.selectedMonth !== m) {
          this.selectedMonth = m;
          this.onYearMonthChange();
        }
      });
    this.loadData();
  }

  loadData() {
    this.loading = true;
    
    // Check if user is authenticated before making API calls
    if (!this.authService.isAuthenticated()) {
      console.warn('User not authenticated, skipping data load');
      this.loading = false;
      // Drop any pending handoff: projects never loaded, so the Add form cannot be
      // pre-filled. Leaving it armed would pop the dialog open later against whatever
      // month the user had moved to by then.
      this.discardPendingProjectHandoff();
      return;
    }

    // Check month status first
    this.checkMonthStatus();
    
    // Use forkJoin instead of Promise.all for better RxJS integration
    // This allows better error handling and timeout management
    forkJoin({
      allocations: this.allocationService.getAllAllocations(this.selectedYear, this.selectedMonth).pipe(
        catchError(error => {
          // Don't log 403 errors - expected for authorization failures
          if (error.status !== 403) {
            console.error('Error loading allocations:', error);
          }
          // If it's an auth error (401), the interceptor will handle it
          if (error.status === 401) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Authentication Required',
              detail: 'Please sign in again to continue.'
            });
          }
          return of([] as Allocation[]);
        })
      ),
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          // Don't log 403 errors - expected for non-admin users (PM/Finance)
          if (error.status !== 403) {
            console.error('Error loading employees:', error);
          }
          if (error.status === 401 || error.status === 403) {
            return of([]);
          }
          return of([]);
        })
      ),
      projects: this.projectService.getAllProjects(true).pipe(
        catchError(error => {
          // Don't log 403 errors - expected for non-admin users
          if (error.status !== 403) {
            console.error('Error loading projects:', error);
          }
          if (error.status === 401 || error.status === 403) {
            return of([]);
          }
          return of([]);
        })
      ),
      costCenters: this.costCenterService.getAllCostCenters().pipe(
        catchError(error => {
          // Don't log 403 errors - expected for non-admin users (PM/Finance)
          if (error.status !== 403) {
            console.error('Error loading cost centers:', error);
          }
          if (error.status === 401 || error.status === 403) {
            return of([]);
          }
          return of([]);
        })
      ),
      roles: this.roleService.getAllRoles().pipe(
        catchError(error => {
          // Don't log 403 errors - expected for non-admin users (PM/Finance)
          if (error.status !== 403) {
            console.error('Error loading roles:', error);
          }
          if (error.status === 401 || error.status === 403) {
            return of([]);
          }
          return of([]);
        })
      ),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(
        catchError(error => {
          if (error.status !== 403) {
            console.error('Error loading service groups:', error);
          }
          if (error.status === 401 || error.status === 403) {
            return of([]);
          }
          return of([]);
        })
      ),
      summaries: this.allocationService.getMonthlySummaries(this.selectedYear, this.selectedMonth)
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.allocations = data.allocations || [];
        this.monthlySummaries = data.summaries || [];
        this.employees = data.employees || [];
        // includePresales: true — in-pursuit deals are staffable from Lead stage, and so are
        // tracker activities marked allocatable. Badge both so an allocator can tell them
        // apart from committed delivery work, and tell a deal apart from an activity.
        this.projects = (data.projects || [])
          .map(p => ({ ...p, name: this.presalesLabel(p) }));
        // Ensure isActive is set for all cost centers (default to true if missing)
        // Badge presales-only cost centers the same way projects are badged, so the two
        // dropdowns read consistently.
        this.costCenters = (data.costCenters || []).map(cc => ({
          ...cc,
          // displayName carries the badge; `name` stays clean because it is what gets
          // persisted onto the allocation (see onCostCenterChange).
          displayName: cc.isPresales ? `${cc.name} (Presales)` : cc.name,
          isActive: cc.isActive !== undefined ? cc.isActive : true
        }));
        // Load roles and build role options
        this.roles = (data.roles || []).filter(r => r.isActive);
        this.roleOptions = this.roles.map(r => ({
          label: r.title,
          value: r.id
        }));

        // Load service groups
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);

        // Build filter options
        this.buildFilterOptions();

        // Reapply filters to preserve filtered state
        this.applyFilters();

        this.loading = false;

        // Deferred until here: the Add form's project dropdown needs this.projects populated
        // before a project can be pre-selected.
        this.consumePendingProjectHandoff();
      },
      error: (error) => {
        console.error('Error loading data:', error);
        // Don't show error message if it's an auth error - interceptor handles it
        if (error.status !== 401 && error.status !== 403) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load data. Please refresh the page or try again later.'
          });
        }
        this.loading = false;
        this.discardPendingProjectHandoff();
      }
    });
  }

  buildFilterOptions() {
    // Build PM options - get unique PMs from allocations
    const pmMap = new Map<string, string>();
    this.allocations.forEach(a => {
      // Use managerName as the key for PM filter
      if (a.managerName) {
        pmMap.set(a.managerName, a.managerName);
      }
    });

    this.pmOptions = Array.from(pmMap.values())
      .map(name => ({ label: name || 'Unknown', value: name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Build Project options - get unique projects from allocations
    const projectMap = new Map<string, string>();
    this.allocations.forEach(a => {
      if (a.projectId && a.projectName) {
        projectMap.set(a.projectId, a.projectName);
      }
    });

    this.projectOptions = Array.from(projectMap.entries())
      .map(([id, name]) => ({ label: name || 'Unknown', value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Build Cost Center options - get from costCenters array (all available cost centers)
    const costCenterMap = new Map<string, string>();

    // First, add cost centers from allocations
    this.allocations.forEach(a => {
      if (a.costCenterId && a.costCenterName) {
        costCenterMap.set(a.costCenterId, a.costCenterName);
      }
    });

    // Then, add all cost centers from the database
    this.costCenters.forEach(cc => {
      if (cc.id && cc.name) {
        costCenterMap.set(cc.id, (cc as any).displayName ?? cc.name);
      }
    });

    this.costCenterOptions = Array.from(costCenterMap.entries())
      .map(([id, name]) => ({ label: name || 'Unknown', value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Build Service Group options - get from allocations and all available service groups
    const serviceGroupMap = new Map<string, string>();

    // First, add service groups from allocations
    this.allocations.forEach(a => {
      if (a.serviceGroupId && a.serviceGroupName) {
        serviceGroupMap.set(a.serviceGroupId, a.serviceGroupName);
      }
    });

    // Then, add all service groups from the database
    this.serviceGroups.forEach(sg => {
      if (sg.id && sg.name) {
        serviceGroupMap.set(sg.id, sg.name);
      }
    });

    this.serviceGroupOptions = Array.from(serviceGroupMap.entries())
      .map(([id, name]) => ({ label: name || 'Unknown', value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /** Employee id -> list of service group ids (for multi–service-group filter). */
  private getEmployeeServiceGroupIds(employeeId: string): string[] {
    const emp = this.employees.find(e => e.id === employeeId);
    if (!emp) return [];
    if (emp.serviceGroupIds && emp.serviceGroupIds.length > 0) return emp.serviceGroupIds;
    if (emp.serviceGroupId) return [emp.serviceGroupId];
    return [];
  }

  /** Toggle a status-card filter; clicking the same card again clears it. */
  filterByStatus(status: 'fully' | 'over' | 'partial'): void {
    this.selectedStatusFilter = this.selectedStatusFilter === status ? null : status;
    this.applyFilters();
  }

  /** Employee IDs that match the current status filter (computed from full allocation list). */
  private getStatusFilteredEmployeeIds(): Set<string> | null {
    if (!this.selectedStatusFilter) return null;
    const totals = this.getEmployeeWeekTotals();
    const matched = new Set<string>();
    for (const emp of this.activeEmployeesForPeriod) {
      const w = totals.get(emp.id) ?? [0, 0, 0, 0, 0];
      if (this.selectedStatusFilter === 'fully' && w.every(v => Math.round(v) === 100)) matched.add(emp.id);
      if (this.selectedStatusFilter === 'over'  && w.some(v => Math.round(v) > 100))   matched.add(emp.id);
      if (this.selectedStatusFilter === 'partial'
        && !w.every(v => Math.round(v) === 100)
        && !w.some(v => Math.round(v) > 100)
        && !w.every(v => Math.round(v) === 0)) matched.add(emp.id);
    }
    return matched;
  }

  applyFilters() {
    const statusIds = this.getStatusFilteredEmployeeIds();
    // Pre-compute the set of employees who have any week with availability (< 100%)
    const availableIds = this.showOnlyAvailable ? this.getAvailableEmployeeIds() : null;

    this.filteredAllocations = this.allocations.filter(allocation => {
      // Apply status card filter
      if (statusIds !== null && !statusIds.has(allocation.employeeId)) return false;

      // Apply "Has Availability" filter
      if (availableIds !== null && !availableIds.has(allocation.employeeId)) return false;

      // Apply search text filter
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        const matchesSearch =
          allocation.employeeName?.toLowerCase().includes(searchLower) ||
          allocation.projectName?.toLowerCase().includes(searchLower) ||
          allocation.comments?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Apply role filter
      if (this.selectedRoles.length > 0 && !this.selectedRoles.includes(allocation.roleId)) {
        return false;
      }

      // Apply PM filter
      if (this.selectedPMs.length > 0 && allocation.managerName && !this.selectedPMs.includes(allocation.managerName)) {
        return false;
      }

      // Apply project filter
      if (this.selectedProjects.length > 0 && !this.selectedProjects.includes(allocation.projectId)) {
        return false;
      }

      // Apply presales / main project-type filter. isProjectPresales is optional on the model,
      // so coerce: anything not explicitly flagged presales counts as main delivery work.
      if (this.projectTypeFilter !== 'all') {
        const isPresales = allocation.isProjectPresales === true;
        // An activity is always presales too, so the activity test has to come off its own
        // flag rather than being inferred from the presales one.
        const isActivity = isPresales && allocation.isProjectPresalesActivity === true;

        if (this.projectTypeFilter === 'presales' && !isPresales) return false;
        if (this.projectTypeFilter === 'presales-projects' && (!isPresales || isActivity)) return false;
        if (this.projectTypeFilter === 'presales-activities' && !isActivity) return false;
        if (this.projectTypeFilter === 'main' && isPresales) return false;
      }

      // Apply invoice type filter
      if (this.selectedInvoiceTypes.length > 0 && !this.selectedInvoiceTypes.includes(allocation.invoiceType)) {
        return false;
      }

      // Apply cost center filter
      if (this.selectedCostCenters.length > 0 && allocation.costCenterId && !this.selectedCostCenters.includes(allocation.costCenterId)) {
        return false;
      }

      // Apply service group filter: show allocations for employees who belong to at least one selected service group
      if (this.selectedServiceGroups.length > 0) {
        const empSgIds = this.getEmployeeServiceGroupIds(allocation.employeeId);
        const hasMatch = empSgIds.length > 0
          ? empSgIds.some(sgId => this.selectedServiceGroups.includes(sgId))
          : !!(allocation.serviceGroupId && this.selectedServiceGroups.includes(allocation.serviceGroupId));
        if (!hasMatch) return false;
      }

      return true;
    });
    this.buildAllocationDisplayRows();
  }

  /** Employees with any week's total allocation < 100% (i.e., have some availability). */
  private getAvailableEmployeeIds(): Set<string> {
    const totals = this.getEmployeeWeekTotals();
    const matched = new Set<string>();
    for (const emp of this.activeEmployeesForPeriod) {
      const w = totals.get(emp.id) ?? [0, 0, 0, 0, 0];
      if (w.some(v => Math.round(v) < 100)) matched.add(emp.id);
    }
    return matched;
  }

  /** Toggle handler bound to the "Has Availability" checkbox. */
  onAvailabilityFilterChange(): void {
    this.applyFilters();
  }

  /** True if any filter or search is applied (for showing clear icon). */
  hasActiveFilters(): boolean {
    return (
      (this.searchText ?? '').trim().length > 0 ||
      (this.selectedRoles?.length ?? 0) > 0 ||
      (this.selectedPMs?.length ?? 0) > 0 ||
      (this.selectedProjects?.length ?? 0) > 0 ||
      (this.selectedInvoiceTypes?.length ?? 0) > 0 ||
      (this.selectedCostCenters?.length ?? 0) > 0 ||
      (this.selectedServiceGroups?.length ?? 0) > 0 ||
      this.projectTypeFilter !== 'all' ||
      this.selectedStatusFilter !== null ||
      this.showOnlyAvailable
    );
  }

  clearFilters() {
    this.selectedRoles = [];
    this.selectedPMs = [];
    this.selectedProjects = [];
    this.selectedInvoiceTypes = [];
    this.selectedCostCenters = [];
    this.selectedServiceGroups = [];
    this.projectTypeFilter = 'all';
    this.searchText = '';
    this.selectedStatusFilter = null;
    this.showOnlyAvailable = false;
    this.applyFilters();
  }

  onYearMonthChange() {
    this.loadData();
  }

  checkMonthStatus() {
    if (this.selectedYear && this.selectedMonth) {
      this.allocationService.getMonthStatus(this.selectedYear, this.selectedMonth).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (status) => {
          const wasClosed = this.isMonthClosed;
          this.isMonthClosed = status.isClosed;
          // Log status change for debugging
          if (wasClosed !== this.isMonthClosed) {
            console.log(`Month status changed: ${this.selectedMonth}/${this.selectedYear} is now ${this.isMonthClosed ? 'CLOSED' : 'OPEN'}`);
          }
        },
        error: (error) => {
          // If status check fails (404 means no status record = not closed), assume month is not closed
          // 403 means user doesn't have permission, but we'll assume not closed for PM users
          this.isMonthClosed = false;
          if (error.status !== 404 && error.status !== 403) {
            console.error('Error checking month status:', error);
          }
        }
      });
    }
  }

  copyToNextMonth() {
    const monthName = this.getMonthName(this.selectedMonth);
    const nextMonthName = this.getMonthName(this.selectedMonth === Month.December ? Month.January : this.selectedMonth + 1);
    this.confirmationService.confirm({
      message: `Copy all allocations from ${monthName} ${this.selectedYear} to ${nextMonthName}? Existing allocations for the target month may be affected.`,
      header: 'Confirm Copy',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
    const request: CopyRequestDto = {
      month: this.selectedMonth,
      year: this.selectedYear,
      createdBy: null
    };

    this.allocationService.copyToNextMonth(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Allocations copied to next month successfully',
          life: 3000
        });
        this.moveToNextMonth();
      },
      error: (error) => {
        console.error('Error copying to next month:', error);

        let errorMessage = 'Failed to copy allocations to next month';
        let severity: 'error' | 'warn' = 'error';

        const serverMessage = error?.error?.message ?? error?.error?.details ?? error?.message;
        if (serverMessage) {
          errorMessage = serverMessage;
          if (errorMessage.toLowerCase().includes('no allocations found')) {
            severity = 'warn';
          }
        }

        this.messageService.add({
          severity: severity,
          summary: severity === 'warn' ? 'No Data' : 'Error',
          detail: errorMessage,
          life: 5000
        });
      }
    });
      }
    });
  }

  private moveToNextMonth() {
    if (this.selectedMonth === Month.December) {
      this.selectedMonth = Month.January;
      this.selectedYear++;
    } else {
      this.selectedMonth++;
    }
    this.yearMonthFilterService.setYear(this.selectedYear);
    this.yearMonthFilterService.setMonth(this.selectedMonth);
    this.loadData();
  }

  // Getter methods for filtered dropdown options (only active items, not excluded from allocation)
  get activeEmployees(): Employee[] {
    const activeEmployees = this.employees.filter(e => e.isActive === true && !(e.excludeFromAllocation === true));
    
    // In edit mode, include the currently selected employee even if inactive or excluded (to avoid breaking existing allocations)
    if (this.isEditMode && this.selectedAllocation.employeeId) {
      const selectedEmployee = this.employees.find(e => e.id === this.selectedAllocation.employeeId);
      if (selectedEmployee && (!selectedEmployee.isActive || selectedEmployee.excludeFromAllocation)) {
        const alreadyIncluded = activeEmployees.some(e => e.id === selectedEmployee.id);
        if (!alreadyIncluded) {
          return [...activeEmployees, selectedEmployee];
        }
      }
    }
    return activeEmployees;
  }

  /**
   * Dropdown label for a project row. Presales work is badged so it cannot be mistaken for
   * committed delivery, and a tracker activity is badged distinctly from a pursuit — both
   * are presales, but only one is a deal, and they are staffed for different reasons.
   */
  private presalesLabel(project: Project): string {
    if (!project.isPresales) {
      return project.name;
    }
    return project.isPresalesActivity
      ? `${project.name} (Presales Activity)`
      : `${project.name} (Presales)`;
  }

  get activeProjects(): Project[] {
    const activeProjects = this.projects.filter(p => p.isActive === true);

    // In edit mode, include the currently selected project even if inactive (to avoid breaking existing allocations)
    if (this.isEditMode && this.selectedAllocation.projectId) {
      const selectedProject = this.projects.find(p => p.id === this.selectedAllocation.projectId);
      if (selectedProject && !selectedProject.isActive) {
        // Check if already in the list to avoid duplicates
        const alreadyIncluded = activeProjects.some(p => p.id === selectedProject.id);
        if (!alreadyIncluded) {
          return [...activeProjects, selectedProject];
        }
      }
    }
    return activeProjects;
  }

  /** Which of the three kinds of allocatable row the form's project dropdown is showing. */
  formProjectTypes: FormProjectTypeFilter = { main: true, presales: true, activities: true };

  /** Checkbox list driving {@link formProjectTypes}; labels match the badges in the dropdown. */
  readonly formProjectTypeOptions: { label: string; key: keyof FormProjectTypeFilter }[] = [
    { label: 'Main', key: 'main' },
    { label: 'Presales', key: 'presales' },
    { label: 'Presales Activity', key: 'activities' }
  ];

  /** Which bucket a project belongs to. The twin-row flags are the only source of truth. */
  private projectTypeOf(project: Project): keyof FormProjectTypeFilter {
    if (!project.isPresales) return 'main';
    return project.isPresalesActivity ? 'activities' : 'presales';
  }

  /**
   * Options for the Add/Edit form's project dropdown: the active projects, narrowed to the
   * ticked types and badged so presales work cannot be mistaken for committed delivery.
   *
   * The currently-selected project always survives the filter. Otherwise unticking a box
   * while a project of that type is selected would blank the control and silently drop the
   * user's pick — on the Edit form that would mean saving the allocation against nothing.
   */
  get projectDisplayOptions(): DropdownOption[] {
    const selectedId = this.selectedAllocation?.projectId;
    return this.activeProjects
      .filter(p => this.formProjectTypes[this.projectTypeOf(p)] || p.id === selectedId)
      // `name` is already badged at load time (see loadData), so it is used as-is here —
      // re-running presalesLabel would append a second "(Presales)".
      .map(p => ({ label: p.name, value: p.id }));
  }

  /** True when every type is unticked, so the dropdown has nothing to offer. */
  get noProjectTypeSelected(): boolean {
    return !this.formProjectTypes.main && !this.formProjectTypes.presales && !this.formProjectTypes.activities;
  }

  /**
   * What the ticked boxes narrow the list to, as a noun phrase ("Main Project",
   * "Presales Activity", "Project or Activity"). Drives the dropdown's placeholder and
   * filter text so both say what is actually selectable rather than a fixed generic label.
   */
  private get projectTypeNoun(): string {
    const { main, presales, activities } = this.formProjectTypes;
    const picked = [
      main ? 'Main Project' : null,
      presales ? 'Presales Project' : null,
      activities ? 'Presales Activity' : null
    ].filter((p): p is string => p !== null);

    // All three ticked is the default, so it keeps the original generic wording rather
    // than spelling out every type.
    if (picked.length === 0 || picked.length === this.formProjectTypeOptions.length) {
      return 'Project or Activity';
    }
    return picked.length === 1 ? picked[0] : `${picked[0]} or ${picked[1]}`;
  }

  /** Placeholder for the project dropdown, naming whichever types are ticked. */
  get projectPlaceholder(): string {
    if (this.noProjectTypeSelected) {
      return 'Tick a type below to see projects';
    }
    return `Select a ${this.projectTypeNoun}`;
  }

  /** Search box placeholder, kept in step with {@link projectPlaceholder}. */
  get projectFilterPlaceholder(): string {
    return `Search ${this.projectTypeNoun}...`;
  }

  /**
   * Shown when the ticked types yield nothing. Names the type so an empty list reads as a
   * filter with no matches rather than as missing data.
   */
  get projectEmptyMessage(): string {
    return `No ${this.projectTypeNoun} found`;
  }

  /**
   * Tick the box matching a pre-selected project, so a project arriving from the handoff
   * (or an inactive one reopened for edit) is not hidden behind an unticked type.
   */
  private revealProjectType(projectId?: string): void {
    if (!projectId) return;
    const project = this.projects.find(p => p.id === projectId);
    if (project) {
      this.formProjectTypes[this.projectTypeOf(project)] = true;
    }
  }

  get activeCostCenters(): CostCenter[] {
    const activeCostCenters = this.costCenters.filter(cc => cc.isActive === true);
    
    // In edit mode, include the currently selected cost center even if inactive (to avoid breaking existing allocations)
    if (this.isEditMode && this.selectedAllocation.costCenterId) {
      const selectedCostCenter = this.costCenters.find(cc => cc.id === this.selectedAllocation.costCenterId);
      if (selectedCostCenter && !selectedCostCenter.isActive) {
        // Check if already in the list to avoid duplicates
        const alreadyIncluded = activeCostCenters.some(cc => cc.id === selectedCostCenter.id);
        if (!alreadyIncluded) {
          return [...activeCostCenters, selectedCostCenter];
        }
      }
    }
    return activeCostCenters;
  }

  get activeServiceGroups(): ServiceGroup[] {
    const activeServiceGroups = this.serviceGroups.filter(sg => sg.isActive === true);
    
    // In edit mode, include the currently selected service group even if inactive (to avoid breaking existing allocations)
    if (this.isEditMode && this.selectedAllocation.serviceGroupId) {
      const selectedServiceGroup = this.serviceGroups.find(sg => sg.id === this.selectedAllocation.serviceGroupId);
      if (selectedServiceGroup && !selectedServiceGroup.isActive) {
        // Check if already in the list to avoid duplicates
        const alreadyIncluded = activeServiceGroups.some(sg => sg.id === selectedServiceGroup.id);
        if (!alreadyIncluded) {
          return [...activeServiceGroups, selectedServiceGroup];
        }
      }
    }
    return activeServiceGroups;
  }

  /**
   * Active cost centers for the Cost Center dropdown. Auto-populated from the project
   * but the user may override it for specific allocations (e.g. when a team member is
   * billed to a different cost center than the project's default).
   * If the currently-selected cost center is inactive, include it so it stays visible.
   */
  get costCenterDisplayOptions(): DropdownOption[] {
    const activeCostCenters = this.costCenters
      .filter(cc => cc.isActive !== false)
      .map(cc => ({ label: (cc as any).displayName ?? cc.name, value: cc.id }));

    const selectedId = this.selectedAllocation?.costCenterId;
    if (selectedId && !activeCostCenters.some(o => o.value === selectedId)) {
      const selected = this.costCenters.find(cc => cc.id === selectedId);
      if (selected) {
        activeCostCenters.unshift({ label: ((selected as any).displayName ?? selected.name) + ' (inactive)', value: selected.id });
      }
    }

    return activeCostCenters.sort((a, b) => a.label.localeCompare(b.label));
  }

  /** Sync costCenterName when the user manually picks a different cost center. */
  onCostCenterChange(costCenterId: string): void {
    const cc = this.costCenters.find(c => c.id === costCenterId);
    this.selectedAllocation.costCenterName = cc?.name ?? '';
  }

  /** Single-option list for Service Group display (same look as Invoice Type). */
  get serviceGroupDisplayOptions(): DropdownOption[] {
    if (!this.selectedAllocation?.projectId) return [];
    const name = this.selectedAllocation.serviceGroupName?.trim() || '-';
    const id = this.selectedAllocation.serviceGroupId ?? '';
    return [{ label: name, value: id }];
  }

  /**
   * Completes the handoff from a project's Team Allocation tab: opens the Add Allocation form
   * with the project pre-selected, so the user only picks an employee and percentages.
   *
   * Runs once — the query params are cleared afterwards so a refresh or a later month change
   * doesn't reopen the dialog unexpectedly. Month-closed rules still apply, because this goes
   * through showAddDialog() rather than setting displayDialog directly.
   */
  /**
   * Clear a pending handoff without acting on it — used when the data load failed, so the
   * project dropdown was never populated. Leaving the flags armed would make a later,
   * unrelated month change pop the Add dialog open with a stale project.
   */
  private discardPendingProjectHandoff(): void {
    if (!this.pendingOpenForm && !this.pendingProjectId) return;
    this.pendingOpenForm = false;
    this.pendingProjectId = null;
    this.clearHandoffQueryParams();
  }

  /** Strip the handoff params so a reload doesn't repeat it. */
  private clearHandoffQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  private consumePendingProjectHandoff(): void {
    if (!this.pendingOpenForm || !this.pendingProjectId) return;

    const projectId = this.pendingProjectId;
    this.pendingOpenForm = false;
    this.pendingProjectId = null;

    this.clearHandoffQueryParams();

    // Respect the same permission that hides the Add Allocation button: Sales can reach this
    // page and read allocations, but must not be handed a create form the UI denies them.
    if (!this.canAddAllocation()) return;

    if (!this.projects.some(p => p.id === projectId)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Project unavailable',
        detail: 'That project could not be found in the allocation list.',
      });
      return;
    }

    this.showAddDialog(projectId);
  }

  showAddDialog(prefillProjectId?: string) {
    // Refresh month status before showing dialog and wait for it to complete
    if (this.selectedYear && this.selectedMonth) {
      this.allocationService.getMonthStatus(this.selectedYear, this.selectedMonth).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (status) => {
          this.isMonthClosed = status.isClosed;
          // Only show dialog if month is not closed (or user is admin)
          if (!this.isMonthClosed || this.authService.hasAdminAccess()) {
            this.selectedAllocation = this.getEmptyAllocation();
            this.fieldTouched = {};
            this.isEditMode = false;
            this.resetFormProjectTypes();
            this.applyPrefillProject(prefillProjectId);
            this.displayDialog = true;
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Month Closed',
              detail: 'This month is closed. Only Admin users can edit closed months.',
              life: 1000
            });
          }
        },
        error: (error) => {
          // If status check fails, assume month is not closed and show dialog
          this.isMonthClosed = false;
          this.selectedAllocation = this.getEmptyAllocation();
          this.fieldTouched = {};
          this.isEditMode = false;
          this.resetFormProjectTypes();
          this.applyPrefillProject(prefillProjectId);
          this.displayDialog = true;
          if (error.status !== 404 && error.status !== 403) {
            console.error('Error checking month status:', error);
          }
        }
      });
    } else {
      // If no month selected, just show dialog
      this.selectedAllocation = this.getEmptyAllocation();
      this.fieldTouched = {};
      this.isEditMode = false;
      this.resetFormProjectTypes();
      this.applyPrefillProject(prefillProjectId);
      this.displayDialog = true;
    }
  }

  /**
   * Pre-select a project on a freshly opened Add form, deriving invoice type, cost center and
   * service group exactly as picking it from the dropdown would.
   */
  private applyPrefillProject(projectId?: string): void {
    if (!projectId) return;
    this.selectedAllocation.projectId = projectId;
    this.revealProjectType(projectId);
    this.onProjectChange(projectId);
  }

  /**
   * Every type ticked, as if the form had never been opened. Without this the ticks set on a
   * previous Add would persist into the next one, hiding projects the user never chose to hide.
   */
  private resetFormProjectTypes(): void {
    this.formProjectTypes = { main: true, presales: true, activities: true };
  }

  /** Button click handler: stop propagation so table row does not capture it, then open edit dialog. */
  onEditAllocationClick(allocation: Allocation, event?: Event): void {
    event?.stopPropagation?.();
    this.showEditDialog(allocation);
  }

  /** Button click handler: stop propagation so table row does not capture it, then delete. */
  onDeleteAllocationClick(allocation: Allocation, event?: Event): void {
    event?.stopPropagation?.();
    this.deleteAllocation(allocation);
  }

  showEditDialog(allocation: Allocation) {
    // Refresh month status before showing dialog to ensure we have latest status
    if (this.selectedYear && this.selectedMonth) {
      this.allocationService.getMonthStatus(this.selectedYear, this.selectedMonth).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (status) => {
          this.isMonthClosed = status.isClosed;
          // Only show dialog if month is not closed (or user is admin)
          if (!this.isMonthClosed || this.authService.hasAdminAccess()) {
            this.selectedAllocation = { ...allocation };
            this.fieldTouched = {};
            this.resetFormProjectTypes();
            this.revealProjectType(allocation.projectId);
            this.applyProjectCostCenterAndServiceGroup();
            this.isEditMode = true;
            this.displayDialog = true;

            // Calculate available percentage for the employee
            if (this.selectedAllocation.employeeId) {
              const excludeId = this.selectedAllocation.id;
              const monthYearAllocations = this.allocations.filter(a => 
                a.month === this.selectedMonth && 
                a.year === this.selectedYear
              );
              
              this.availablePercentageInfo = this.allocationPredictorService.calculateAvailablePercentage(
                this.selectedAllocation.employeeId,
                monthYearAllocations,
                excludeId
              );
              this.showAvailablePercentageCard = true;
              
              // Update remaining percentages for all weeks
              for (let week = 1; week <= 5; week++) {
                this.updateWeeklyRemaining(week);
              }
            }
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Month Closed',
              detail: 'This month is closed. Only Admin users can edit closed months.',
              life: 1000
            });
          }
        },
        error: (error) => {
          // If status check fails, assume month is not closed and show dialog
          this.isMonthClosed = false;
          this.selectedAllocation = { ...allocation };
          this.fieldTouched = {};
          this.resetFormProjectTypes();
          this.revealProjectType(allocation.projectId);
          this.applyProjectCostCenterAndServiceGroup();
          this.isEditMode = true;
          this.displayDialog = true;
          
          // Calculate available percentage for the employee
          if (this.selectedAllocation.employeeId) {
            const excludeId = this.selectedAllocation.id;
            const monthYearAllocations = this.allocations.filter(a => 
              a.month === this.selectedMonth && 
              a.year === this.selectedYear
            );
            
            this.availablePercentageInfo = this.allocationPredictorService.calculateAvailablePercentage(
              this.selectedAllocation.employeeId,
              monthYearAllocations,
              excludeId
            );
            this.showAvailablePercentageCard = true;
            
            // Update remaining percentages for all weeks
            for (let week = 1; week <= 5; week++) {
              this.updateWeeklyRemaining(week);
            }
          }
          if (error.status !== 404 && error.status !== 403) {
            console.error('Error checking month status:', error);
          }
        }
      });
    } else {
      // If no month selected, just show dialog
      this.selectedAllocation = { ...allocation };
      this.fieldTouched = {};
      this.isEditMode = true;
      this.displayDialog = true;
      
      // Calculate available percentage for the employee
      if (this.selectedAllocation.employeeId) {
        const excludeId = this.selectedAllocation.id;
        const monthYearAllocations = this.allocations.filter(a => 
          a.month === this.selectedMonth && 
          a.year === this.selectedYear
        );
        
        this.availablePercentageInfo = this.allocationPredictorService.calculateAvailablePercentage(
          this.selectedAllocation.employeeId,
          monthYearAllocations,
          excludeId
        );
        this.showAvailablePercentageCard = true;
        
        // Update remaining percentages for all weeks
        for (let week = 1; week <= 5; week++) {
          this.updateWeeklyRemaining(week);
        }
      }
    }
  }

  hideDialog() {
    this.displayDialog = false;
    // Reset form data when dialog is closed
    this.selectedAllocation = this.getEmptyAllocation();
    this.fieldTouched = {};
    this.isEditMode = false;
    this.availablePercentageInfo = null;
    this.showAvailablePercentageCard = false;
    this.weeklyRemainingPercentage = {};
  }

  /** Reset form to empty state (keeps dialog open). */
  resetAllocationForm() {
    this.selectedAllocation = this.getEmptyAllocation();
    this.isEditMode = false;
    this.availablePercentageInfo = null;
    this.showAvailablePercentageCard = false;
    this.weeklyRemainingPercentage = {};
  }

  deleteAllocation(allocation: Allocation) {
    // Refresh month status before delete to ensure we have latest status
    this.checkMonthStatus();
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this allocation for "${allocation.employeeName}" on "${allocation.projectName}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.allocationService.deleteAllocation(allocation.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Allocation deleted successfully'
            });
            this.loadData();
          },
          error: (error) => {
            console.error('Error deleting allocation:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete allocation'
            });
          }
        });
      }
    });
  }

  onEmployeeChange(employeeId: string) {
    // Auto-select employee's role and service group; filter role dropdown to assigned roles only
    if (employeeId) {
      const selectedEmployee = this.employees.find(e => e.id === employeeId);
      if (selectedEmployee) {
        const assignedRoleIds = (selectedEmployee.roleIds && selectedEmployee.roleIds.length > 0)
          ? selectedEmployee.roleIds
          : (selectedEmployee.roleId ? [selectedEmployee.roleId] : []);
        const primaryRoleId = assignedRoleIds.length > 0 ? assignedRoleIds[0] : selectedEmployee.roleId;
        if (primaryRoleId) {
          this.selectedAllocation.roleId = primaryRoleId;
          this.onRoleChange(primaryRoleId);
        }
        if (selectedEmployee.serviceGroupId) {
          this.selectedAllocation.serviceGroupId = selectedEmployee.serviceGroupId;
        }
      }
      
      // Calculate available percentage for the selected employee
      const excludeId = this.isEditMode ? this.selectedAllocation.id : undefined;
      const monthYearAllocations = this.allocations.filter(a =>
        a.year === this.selectedYear && a.month === this.selectedMonth
      );
      this.availablePercentageInfo = this.allocationPredictorService.calculateAvailablePercentage(
        employeeId,
        monthYearAllocations,
        excludeId
      );
      this.showAvailablePercentageCard = true;
    } else {
      this.availablePercentageInfo = null;
      this.showAvailablePercentageCard = false;
    }
  }

  onProjectChange(projectId: string) {
    if (!projectId) return;
    const selectedProject = this.projects.find(p => p.id === projectId);
    if (!selectedProject) return;
    // Auto-select invoice type based on project
    if (selectedProject.invoiceType !== undefined) {
      this.selectedAllocation.invoiceType = selectedProject.invoiceType;
    }
    this.applyProjectCostCenterAndServiceGroup();
  }

  /** Set Cost Center and Service Group from the selected project (no user dropdown). */
  private applyProjectCostCenterAndServiceGroup(): void {
    const projectId = this.selectedAllocation?.projectId;
    if (!projectId) return;
    const project = this.projects.find(p => p.id === projectId);
    if (!project) return;
    this.selectedAllocation.costCenterId = project.costCenterId ?? undefined;
    this.selectedAllocation.costCenterName = project.costCenterName ?? '';
    this.selectedAllocation.serviceGroupId = project.serviceGroupId ?? undefined;
    this.selectedAllocation.serviceGroupName = project.serviceGroupName ?? '';
  }

  onRoleChange(_roleId: string) {
    // No special handling needed (Reports To field removed)
  }

  /**
   * Add suggested available percentages to current allocation for all weeks (capped at 100% per week).
   */
  fillWithAvailablePercentages() {
    if (!this.availablePercentageInfo) {
      this.showSingleMessage('warn', 'No Data', 'Please select an employee first');
      return;
    }

    const suggested = this.allocationPredictorService.getSuggestedAllocation(this.availablePercentageInfo);
    const current = [
      Number(this.selectedAllocation.week1Percentage) || 0,
      Number(this.selectedAllocation.week2Percentage) || 0,
      Number(this.selectedAllocation.week3Percentage) || 0,
      Number(this.selectedAllocation.week4Percentage) || 0,
      Number(this.selectedAllocation.week5Percentage) || 0
    ];
    this.selectedAllocation.week1Percentage = Math.min(100, current[0] + (suggested[0] ?? 0));
    this.selectedAllocation.week2Percentage = Math.min(100, current[1] + (suggested[1] ?? 0));
    this.selectedAllocation.week3Percentage = Math.min(100, current[2] + (suggested[2] ?? 0));
    this.selectedAllocation.week4Percentage = Math.min(100, current[3] + (suggested[3] ?? 0));
    this.selectedAllocation.week5Percentage = Math.min(100, current[4] + (suggested[4] ?? 0));

    for (let week = 1; week <= 5; week++) {
      this.updateWeeklyRemaining(week);
    }

    this.showSingleMessage('success', 'Success', 'Available capacity added to current allocation');
  }

  /**
   * Add the available (remaining) percentage to the current week allocation.
   * E.g. 5% already assigned + 80% remaining → 85% (not replace with 80%).
   */
  // "Set all → Apply" quick-fill, matching the Future Allocation form. Writes the same value into
  // every week, routed through onWeekPercentageChange so the remaining/over badges recalculate
  // exactly as they do for a manual edit.
  applyAllValue: number | null = 100;

  applyAllWeeks(): void {
    const value = this.applyAllValue ?? 0;
    for (let week = 1; week <= 5; week++) {
      if (this.isWeekInputDisabled(week)) {
        continue;
      }
      (this.selectedAllocation as any)[`week${week}Percentage`] = value;
      this.onWeekPercentageChange(week, value);
    }
  }

  fillWeekWithAvailable(week: number) {
    if (!this.availablePercentageInfo) {
      return;
    }

    const remaining = this.getRemainingPercentage(week);
    if (remaining <= 0) {
      this.showSingleMessage('warn', 'No Capacity Available', `Week ${week} has no remaining capacity available`, 3000);
      return;
    }

    const weekKey = `week${week}Percentage` as keyof Allocation;
    const currentValue = Number((this.selectedAllocation as any)[weekKey]) || 0;
    const valueToSet = Math.min(100, currentValue + remaining);
    (this.selectedAllocation as any)[weekKey] = valueToSet;
    this.updateWeeklyRemaining(week);
  }

  /**
   * Update remaining percentage for a specific week
   */
  updateWeeklyRemaining(week: number) {
    if (!this.availablePercentageInfo) {
      return;
    }

    const baseAvailablePercentages = [
      this.availablePercentageInfo.week1Available,
      this.availablePercentageInfo.week2Available,
      this.availablePercentageInfo.week3Available,
      this.availablePercentageInfo.week4Available,
      this.availablePercentageInfo.week5Available
    ];

    const baseAvailable = baseAvailablePercentages[week - 1];
    
    // Ensure baseAvailable is a valid number
    const baseAvailableNum = (baseAvailable != null && baseAvailable !== undefined) ? Number(baseAvailable) : 0;
    if (isNaN(baseAvailableNum)) {
      this.weeklyRemainingPercentage[week] = 0;
      return;
    }

    const weekKey = `week${week}Percentage` as keyof Allocation;
    const enteredValue = (this.selectedAllocation as any)[weekKey];
    
    // Convert to number, handling null, undefined, empty string, or invalid values
    let enteredPercentage = 0;
    if (enteredValue != null && enteredValue !== undefined && enteredValue !== '') {
      const numValue = Number(enteredValue);
      if (!isNaN(numValue)) {
        enteredPercentage = numValue;
      }
    }

    const remaining = baseAvailableNum - enteredPercentage;
    this.weeklyRemainingPercentage[week] = Math.round(remaining * 100) / 100; // Round to 2 decimal places, allow negative for over-allocation
  }

  /**
   * Clear all previous messages before showing a new one
   */
  private clearPreviousMessages(): void {
    this.messageService.clear();
  }

  /**
   * Show a single message (clears previous messages first)
   */
  // styleClass lets a caller tag the toast (e.g. 'toast-over-allocated' for the violet
  // over-allocation variant, so the toast matches the fields it is warning about).
  private showSingleMessage(severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string, life?: number, styleClass?: string): void {
    this.clearPreviousMessages();
    this.messageService.add({
      severity,
      summary,
      detail,
      life: life || 1000,
      styleClass
    });
  }

  /**
   * Validate and limit week percentage input to not exceed remaining
   */
  onWeekPercentageChange(week: number, value: any): void {
    if (!this.availablePercentageInfo) {
      return;
    }

    const weekKey = `week${week}Percentage` as keyof Allocation;
    
    // If value is null, undefined, or empty, allow it (clearing the field)
    if (value == null || value === undefined || value === '') {
      (this.selectedAllocation as any)[weekKey] = value;
      this.updateWeeklyRemaining(week);
      return;
    }

    // Convert to number - handle string values like "85%" or "85"
    let valueToConvert = value;
    if (typeof valueToConvert === 'string') {
      valueToConvert = valueToConvert.replace('%', '').trim();
    }
    const numValue = Number(valueToConvert);
    
    if (isNaN(numValue) || numValue < 0) {
      // Invalid number, revert to previous value or 0
      const currentValue = (this.selectedAllocation as any)[weekKey] || 0;
      (this.selectedAllocation as any)[weekKey] = currentValue;
      return;
    }

    // Get the base available for this week
    const baseAvailablePercentages = [
      this.availablePercentageInfo.week1Available,
      this.availablePercentageInfo.week2Available,
      this.availablePercentageInfo.week3Available,
      this.availablePercentageInfo.week4Available,
      this.availablePercentageInfo.week5Available
    ];
    const baseAvailable = baseAvailablePercentages[week - 1];
    const baseAvailableNum = (baseAvailable != null && baseAvailable !== undefined) ? Number(baseAvailable) : 0;

    // Allow values above available capacity (over-allocation) - just warn the user
    if (numValue > baseAvailableNum && baseAvailableNum >= 0) {
      // Calculate total allocation: (100 - baseAvailableNum) is already allocated, add the new value
      const alreadyAllocated = 100 - baseAvailableNum;
      const totalAllocation = alreadyAllocated + numValue;
      // Warn about over-allocation but allow it
      this.showSingleMessage('warn', 'Over-Allocation Warning', `Week ${week} allocation exceeds available capacity. Total allocation will be ${totalAllocation.toFixed(1)}%.`, 4000, 'toast-over-allocated');
    }
    
    // Store as number (not string) - allow any value >= 0
    (this.selectedAllocation as any)[weekKey] = numValue;

    // Update remaining after setting the value
    this.updateWeeklyRemaining(week);
    
    // Force change detection to update the display immediately
    // The getRemainingPercentage method will be called by Angular's change detection
  }

  /**
   * Check if a week input should be disabled (never disable - allow over-allocation)
   */
  isWeekInputDisabled(week: number): boolean {
    // Always allow input to enable over-allocation
    return false;
  }

  /**
   * Get filtered options for a week (only show values <= base available)
   */
  getWeekPercentageOptions(week: number): DropdownOption[] {
    if (!this.availablePercentageInfo) {
      return this.allocationPercentageOptions;
    }

    const baseAvailablePercentages = [
      this.availablePercentageInfo.week1Available,
      this.availablePercentageInfo.week2Available,
      this.availablePercentageInfo.week3Available,
      this.availablePercentageInfo.week4Available,
      this.availablePercentageInfo.week5Available
    ];
    const baseAvailable = baseAvailablePercentages[week - 1];
    const baseAvailableNum = (baseAvailable != null && baseAvailable !== undefined) ? Number(baseAvailable) : 0;
    
    // Allow all percentage options (including values above 100% for over-allocation)
    // The user can still type custom values above 100% even if not in dropdown
    return this.allocationPercentageOptions;
  }

  /**
   * Get remaining percentage for a week
   * Shows available capacity immediately when employee is selected,
   * then shows remaining after user enters values
   * In edit mode, this shows remaining after the current allocation is considered
   */
  getRemainingPercentage(week: number): number {
    // Explicitly ensure return type is number
    if (!this.availablePercentageInfo) {
      return 0;
    }

    // Get the base available percentage for this week (excluding current allocation in edit mode)
    const baseAvailablePercentages = [
      this.availablePercentageInfo.week1Available,
      this.availablePercentageInfo.week2Available,
      this.availablePercentageInfo.week3Available,
      this.availablePercentageInfo.week4Available,
      this.availablePercentageInfo.week5Available
    ];

    const baseAvailable = baseAvailablePercentages[week - 1];
    
    // Ensure baseAvailable is a valid number
    const baseAvailableNum = (baseAvailable != null && baseAvailable !== undefined) ? Number(baseAvailable) : 0;
    if (isNaN(baseAvailableNum)) {
      return 0;
    }

    // Get the entered percentage for this week
    const weekKey = `week${week}Percentage` as keyof Allocation;
    const enteredValue = (this.selectedAllocation as any)[weekKey];
    
    // Convert to number, handling null, undefined, empty string, or invalid values
    // Also handle string values like "85%" or "85"
    let enteredPercentage = 0;
    if (enteredValue != null && enteredValue !== undefined && enteredValue !== '') {
      // If it's a string, remove % sign if present
      let valueToConvert = enteredValue;
      if (typeof valueToConvert === 'string') {
        valueToConvert = valueToConvert.replace('%', '').trim();
      }
      const numValue = Number(valueToConvert);
      if (!isNaN(numValue) && numValue >= 0) {
        enteredPercentage = numValue;
      }
    }

    // Calculate remaining: base available (excluding current) - current allocation value
    // This gives the actual remaining capacity (negative means over-allocation)
    const remaining: number = Number(baseAvailableNum) - Number(enteredPercentage);
    const result: number = Math.round(remaining * 100) / 100;
    
    return result;
  }

  /**
   * Get the available percentage to display in the Available Capacity card
   * In edit mode, this should show remaining after current allocation
   */
  getAvailableCapacityForDisplay(week: number): number {
    if (!this.availablePercentageInfo) {
      return 0;
    }

    // Get the base available (excluding current allocation in edit mode)
    const baseAvailablePercentages = [
      this.availablePercentageInfo.week1Available,
      this.availablePercentageInfo.week2Available,
      this.availablePercentageInfo.week3Available,
      this.availablePercentageInfo.week4Available,
      this.availablePercentageInfo.week5Available
    ];

    const baseAvailable = baseAvailablePercentages[week - 1];
    const baseAvailableNum = (baseAvailable != null && baseAvailable !== undefined) ? Number(baseAvailable) : 0;
    
    if (isNaN(baseAvailableNum)) {
      return 0;
    }

    // In edit mode, show remaining after current allocation
    if (this.isEditMode && this.selectedAllocation) {
      const weekKey = `week${week}Percentage` as keyof Allocation;
      const currentValue = (this.selectedAllocation as any)[weekKey] || 0;
      const currentNum = Number(currentValue) || 0;
      
      // Remaining = base available (without current) - current allocation value.
      // Clamped at 0: this card reports AVAILABLE capacity, which bottoms out at zero — a negative
      // "available" reads as nonsense. Over-allocation is flagged on the week fields and the
      // Over/Remaining badge instead, which is where the excess was entered.
      const remaining = Math.max(0, baseAvailableNum - currentNum);
      return Math.round(remaining * 100) / 100;
    }

    // In add mode, show base available
    return baseAvailableNum;
  }

  saveAllocation() {
    // Mark fields as touched so inline validation messages appear
    this.fieldTouched['employeeId'] = true;
    this.fieldTouched['projectId'] = true;

    // Validate required fields
    if (!this.selectedAllocation.employeeId || !this.selectedAllocation.projectId) {
      return;
    }

    if (this.isEditMode) {
      this.allocationService.updateAllocation(this.selectedAllocation.id, this.selectedAllocation).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.showSingleMessage('success', 'Success', 'Allocation updated successfully');
          this.loadData();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating allocation:', error);
          
          // Extract validation errors from the response - show only first error
          let errorMessage = 'Failed to update allocation';
          if (error.error?.errors) {
            // FluentValidation returns errors as an object with property names as keys
            const validationErrors = error.error.errors;
            const errorMessages: string[] = [];
            
            for (const key in validationErrors) {
              if (validationErrors.hasOwnProperty(key)) {
                const messages = validationErrors[key];
                if (Array.isArray(messages)) {
                  errorMessages.push(...messages);
                } else {
                  errorMessages.push(messages);
                }
              }
            }
            
            // Show only the first error message
            if (errorMessages.length > 0) {
              errorMessage = errorMessages[0];
            }
          } else if (error.error?.title) {
            errorMessage = error.error.title;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }
          
          this.showSingleMessage('error', 'Error', errorMessage);
        }
      });
    } else {
      const createDto: CreateAllocationDto = {
        employeeId: this.selectedAllocation.employeeId,
        roleId: this.selectedAllocation.roleId,
        projectId: this.selectedAllocation.projectId,
        invoiceType: this.selectedAllocation.invoiceType,
        costCenterId: this.selectedAllocation.costCenterId,
        serviceGroupId: this.selectedAllocation.serviceGroupId,
        reportsToEmployeeId: undefined,
        month: this.selectedMonth,
        year: this.selectedYear,
        week1Percentage: this.selectedAllocation.week1Percentage,
        week2Percentage: this.selectedAllocation.week2Percentage,
        week3Percentage: this.selectedAllocation.week3Percentage,
        week4Percentage: this.selectedAllocation.week4Percentage,
        week5Percentage: this.selectedAllocation.week5Percentage,
        comments: this.selectedAllocation.comments
      };

      this.allocationService.createAllocation(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.showSingleMessage('success', 'Success', 'Allocation created successfully');
          this.loadData();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error creating allocation:', error);
          
          // Extract validation errors - show only first error
          let errorMessage = 'Failed to create allocation';
          if (error.error?.errors) {
            const validationErrors = error.error.errors;
            const errorMessages: string[] = [];
            
            for (const key in validationErrors) {
              if (validationErrors.hasOwnProperty(key)) {
                const messages = validationErrors[key];
                if (Array.isArray(messages)) {
                  errorMessages.push(...messages);
                } else {
                  errorMessages.push(messages);
                }
              }
            }
            
            if (errorMessages.length > 0) {
              errorMessage = errorMessages[0];
            }
          } else if (error.error?.title) {
            errorMessage = error.error.title;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }
          
          this.showSingleMessage('error', 'Error', errorMessage);
        }
      });
    }
  }

  getRoleName(roleId: string): string {
    const role = this.roles.find(r => r.id === roleId);
    return role ? role.title : 'Unknown';
  }

  /** Initials for employee avatar (e.g. "Ashvani Jayaratne" -> "AJ"). */
  getEmployeeInitials(name: string | null | undefined): string {
    if (!name || !name.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  }

  getInvoiceTypeName(invoiceType: InvoiceType): string {
    switch (invoiceType) {
      case InvoiceType.Monthly:
        return 'Monthly';
      case InvoiceType.ProjectMilestone:
        return 'Project Milestone';
      case InvoiceType.NotAvailable:
        return 'N/A';
      default:
        return 'Unknown';
    }
  }

  /** Short label for invoice badge (e.g. "Proj. Milestone"). */
  getInvoiceTypeLabel(invoiceType: InvoiceType): string {
    switch (invoiceType) {
      case InvoiceType.Monthly:
        return 'Monthly';
      case InvoiceType.ProjectMilestone:
        return 'Proj. Milestone';
      case InvoiceType.NotAvailable:
        return 'N/A';
      default:
        return 'Unknown';
    }
  }

  getInvoiceTypeSeverity(invoiceType: InvoiceType): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (invoiceType) {
      case InvoiceType.Monthly:
        return 'success';
      case InvoiceType.ProjectMilestone:
        return 'warn';
      case InvoiceType.NotAvailable:
        return 'info';
      default:
        return 'secondary';
    }
  }

  getPmDisplayName(allocation: Allocation): string {
    // If the employee is a Project Manager, show who they report to
    if (allocation.roleTitle === 'Project Manager') {
      return allocation.reportsToEmployee?.name || '-';
    }
    // For other roles, show the project manager
    return allocation.managerName || '-';
  }

  /** Full project + TL/PM text for hover tooltip. */
  getProjectCellTooltip(allocation: Allocation): string {
    const pm = allocation.managerName || '—';
    const parts = [allocation.projectName || ''];
    if (pm) parts.push(`PM: ${pm}`);
    return parts.filter(Boolean).join('\n');
  }

  /** Severity for week % bar/text: normal (blue), zero (amber), half (50%, green), full (100%, blue), over (>100%, red), empty (dash). */
  getWeekPercentSeverity(pct: number | string | null | undefined): 'empty' | 'zero' | 'half' | 'normal' | 'full' | 'over' {
    if (pct == null || pct === '') return 'empty';
    const n = Number(pct);
    if (Number.isNaN(n)) return 'empty';
    if (n === 0) return 'zero';
    if (n === 50) return 'half';
    if (n === 100) return 'full';
    if (n > 100) return 'over';
    return 'normal';
  }

  getRowNumber(allocation: Allocation): number {
    const index = this.allocations.findIndex(a => a.id === allocation.id);
    return index + 1;
  }

  /** True if any week's allocation is less than 100 (0–99) or more than 100 (101+) – highlight in red. All five weeks exactly 100 = no highlight. */
  isRowAllocationNot100(allocation: Allocation): boolean {
    const weeks = [
      Number(allocation.week1Percentage) ?? 0,
      Number(allocation.week2Percentage) ?? 0,
      Number(allocation.week3Percentage) ?? 0,
      Number(allocation.week4Percentage) ?? 0,
      Number(allocation.week5Percentage) ?? 0
    ];
    return weeks.some(w => w < 100 || w > 100);
  }

  onSearch() {
    this.applyFilters();
  }

  onSearchInput(event: any) {
    const query = event.query.toLowerCase();
    const suggestions: any[] = [];
    const seen = new Set<string>();

    if (query.length === 0) {
      this.searchSuggestions = [];
      return;
    }

    // Search in employee names
    this.allocations.forEach(a => {
      if (a.employeeName?.toLowerCase().includes(query) && !seen.has(a.employeeName)) {
        suggestions.push({
          label: a.employeeName,
          type: 'Employee',
          value: a.employeeName
        });
        seen.add(a.employeeName);
      }
    });

    // Search in project names
    this.allocations.forEach(a => {
      if (a.projectName?.toLowerCase().includes(query) && !seen.has(a.projectName)) {
        suggestions.push({
          label: a.projectName,
          type: 'Project',
          value: a.projectName
        });
        seen.add(a.projectName);
      }
    });

    // Search in cost center names
    this.allocations.forEach(a => {
      if (a.costCenterName?.toLowerCase().includes(query) && !seen.has(a.costCenterName)) {
        suggestions.push({
          label: a.costCenterName,
          type: 'Cost Center',
          value: a.costCenterName
        });
        seen.add(a.costCenterName);
      }
    });

    this.searchSuggestions = suggestions.slice(0, 10); // Limit to 10 suggestions
  }

  onSearchSelect(event: any) {
    this.searchText = event.value;
    this.applyFilters();
  }

  clearSearch() {
    this.searchText = '';
    this.applyFilters();
  }

  private getEmptyAllocation(): Allocation {
    // Get first active role as default, or empty string if no roles loaded
    const defaultRoleId = this.roles.length > 0 ? this.roles[0].id : '';
    return {
      id: '',
      employeeId: '',
      employeeName: '',
      roleId: defaultRoleId,
      roleTitle: '',
      projectId: '',
      projectName: '',
      invoiceType: undefined as unknown as InvoiceType,
      costCenterId: undefined,
      costCenterName: '',
      serviceGroupId: undefined,
      serviceGroupName: '',
      managerName: '',
      reportsToEmployeeId: undefined,
      month: this.selectedMonth,
      year: this.selectedYear,
      week1Percentage: 0,
      week2Percentage: 0,
      week3Percentage: 0,
      week4Percentage: 0,
      week5Percentage: 0,
      comments: '',
      reportsToEmployee: undefined
    };
  }

  getMonthName(month: Month): string {
    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthNames[month] || '';
  }

  /**
   * Names the filters that shaped the export, so a saved file can be traced back to the view it
   * came from. Only active filters are listed — an unfiltered export says so explicitly rather
   * than listing every control as "All".
   */
  private buildExportFilterContext(): { label: string; value: string }[] {
    const context: { label: string; value: string }[] = [];
    const labelFor = (options: any[], value: unknown) =>
      options.find(o => o.value === value)?.label ?? String(value);

    if (this.projectTypeFilter !== 'all') {
      context.push({ label: 'Project Type', value: labelFor(this.projectTypeOptions, this.projectTypeFilter) });
    }
    if (this.selectedRoles.length) {
      context.push({ label: 'Role', value: this.selectedRoles.map(v => labelFor(this.roleOptions, v)).join(', ') });
    }
    if (this.selectedPMs.length) {
      context.push({ label: 'PM', value: this.selectedPMs.join(', ') });
    }
    if (this.selectedProjects.length) {
      context.push({ label: 'Project', value: this.selectedProjects.map(v => labelFor(this.projectOptions, v)).join(', ') });
    }
    if (this.selectedInvoiceTypes.length) {
      context.push({ label: 'Invoice Type', value: this.selectedInvoiceTypes.map(v => labelFor(this.invoiceTypeOptions, v)).join(', ') });
    }
    if (this.selectedCostCenters.length) {
      context.push({ label: 'Cost Center', value: this.selectedCostCenters.map(v => labelFor(this.costCenterOptions, v)).join(', ') });
    }
    if (this.selectedServiceGroups.length) {
      context.push({ label: 'Service Group', value: this.selectedServiceGroups.map(v => labelFor(this.serviceGroupOptions, v)).join(', ') });
    }
    if (this.showOnlyAvailable) {
      context.push({ label: 'Availability', value: 'Has availability only' });
    }
    if (this.selectedStatusFilter) {
      const statusLabels: Record<'fully' | 'over' | 'partial', string> = {
        fully: 'Fully allocated',
        over: 'Over allocated',
        partial: 'Partially allocated'
      };
      context.push({ label: 'Status', value: statusLabels[this.selectedStatusFilter] });
    }
    if ((this.searchText ?? '').trim()) {
      context.push({ label: 'Search', value: this.searchText.trim() });
    }

    if (context.length === 0) {
      context.push({ label: 'Filters', value: 'None — all allocations for the period' });
    }
    return context;
  }

  private formatPercentage(value: number): string {
    return `${value}%`;
  }

  exportToExcel() {
    // Export what the user is looking at, not the whole month. Placeholder "Available" rows are
    // excluded because they are not real allocations — they only visualise remaining capacity, and
    // including them would double-count against the workbook's own Total / Available rows.
    const exportRows = (this.filteredAllocations ?? []).filter(
      a => (a.projectName ?? '').trim().toLowerCase() !== 'available'
    );

    if (exportRows.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No allocations to export'
      });
      return;
    }

    const monthName = this.getMonthName(this.selectedMonth);

    const request: AllocationExportRequest = {
      periodLabel: `${monthName}_${this.selectedYear}`,
      filters: this.buildExportFilterContext(),
      // Summary rows only make sense across an employee's whole month. Narrowing to presales
      // or main would make "Total" the total of the slice and "Available" overstate free
      // capacity, since the rows that fill it are filtered out of the file.
      includeSummaryRows: this.projectTypeFilter === 'all',
      rows: exportRows
        // Keep each employee's rows contiguous so the per-employee Total / Available rows in the
        // workbook attach to the right block.
        .slice()
        .sort((a, b) =>
          (a.employeeName ?? '').localeCompare(b.employeeName ?? '') ||
          (a.projectName ?? '').localeCompare(b.projectName ?? ''))
        .map(a => ({
          employeeId: a.employeeId,
          employeeName: a.employeeName ?? '',
          roleTitle: a.roleTitle ?? '',
          designationName: a.designationName ?? '',
          categoryName: a.categoryName ?? '',
          managerName: a.managerName ?? '',
          invoiceTypeName: this.getInvoiceTypeName(a.invoiceType),
          // Resolve exactly as the grid does. Allocations frequently do not persist a cost
          // center of their own — it is owned by the Project — so sending the raw field
          // exported a blank column for most presales rows while the screen showed a name.
          costCenterName: this.resolveExportCostCenterName(a),
          serviceGroupName: a.serviceGroupName ?? '',
          projectName: a.projectName ?? '',
          isProjectPresales: a.isProjectPresales === true,
          isProjectPresalesActivity: a.isProjectPresalesActivity === true,
          weeks: [
            a.week1Percentage ?? 0,
            a.week2Percentage ?? 0,
            a.week3Percentage ?? 0,
            a.week4Percentage ?? 0,
            a.week5Percentage ?? 0
          ],
          comments: a.comments ?? ''
        }))
    };

    this.allocationService.exportToExcel(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (blob: Blob) => {
        // Every filtered export needs its own suffix, or two differently-scoped files land
        // in Downloads under the same name.
        const suffixByType: Record<string, string> = {
          presales: '_Presales',
          'presales-projects': '_PresalesProjects',
          'presales-activities': '_PresalesActivities',
          main: '_Main'
        };
        const suffix = suffixByType[this.projectTypeFilter] ?? '';
        const fileName = `TeamAllocation_${monthName}_${this.selectedYear}${suffix}.xlsx`;

        saveAs(blob, fileName);

        this.messageService.add({
          severity: 'success',
          summary: 'Export Successful',
          detail: `File exported as ${fileName}`
        });
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: error.error?.message || 'Failed to export allocations to Excel'
        });
      }
    });
  }



  // Role-based permission checks
  canAddAllocation(): boolean {
    // If month is closed, only Admin can add
    if (this.isMonthClosed) {
      return this.authService.hasAdminAccess();
    }
    // If month is open (not closed), PM can add to the selected month
    return this.authService.hasPMAccess();
  }

  canEditAllocation(allocation: Allocation): boolean {
    // If month is closed, only Admin can edit
    if (this.isMonthClosed && allocation.month === this.selectedMonth && allocation.year === this.selectedYear) {
      return this.authService.hasAdminAccess();
    }

    // Admin can edit any month (open or closed)
    if (this.authService.hasAdminAccess()) {
      return true;
    }

    // PM can edit if:
    // 1. Month is NOT closed (reopened or never closed)
    // 2. AND it's the current month OR it's the selected month (for reopened months)
    if (this.authService.hasPMAccess()) {
      // If month is open (not closed), PM can edit the selected month
      if (!this.isMonthClosed && allocation.month === this.selectedMonth && allocation.year === this.selectedYear) {
        return true;
      }
      // Otherwise, PM can only edit current month
      return this.isCurrentMonth(allocation.month, allocation.year);
    }

    return false;
  }

  canDeleteAllocation(allocation: Allocation): boolean {
    // If month is closed, only Admin can delete
    if (this.isMonthClosed && allocation.month === this.selectedMonth && allocation.year === this.selectedYear) {
      return this.authService.hasAdminAccess();
    }

    // Admin can delete any month (open or closed)
    if (this.authService.hasAdminAccess()) {
      return true;
    }

    // PM can delete if:
    // 1. Month is NOT closed (reopened or never closed)
    // 2. AND it's the current month OR it's the selected month (for reopened months)
    if (this.authService.hasPMAccess()) {
      // If month is open (not closed), PM can delete the selected month
      if (!this.isMonthClosed && allocation.month === this.selectedMonth && allocation.year === this.selectedYear) {
        return true;
      }
      // Otherwise, PM can only delete current month
      return this.isCurrentMonth(allocation.month, allocation.year);
    }

    return false;
  }

  canCopyToNextMonth(): boolean {
    return this.authService.hasAdminAccess();
  }

  // Whether to show the Actions column at all. Read-only users (e.g. Sales) never write,
  // so the whole column is hidden for them rather than showing empty cells.
  get canShowActionsColumn(): boolean {
    return this.authService.hasPMAccess();
  }

  canExportToExcel(): boolean {
    // Permission matrix: Download Excel - Admin, PM, Finance, Sales (read-only Sales can export)
    return this.authService.hasPMAccess() || this.authService.hasFinanceAccess() || this.authService.getUser()?.isSales === true;
  }

  canCloseMonth(): boolean {
    // Only Admin can close month, and only if month is not already closed
    return this.authService.hasAdminAccess() && !this.isMonthClosed;
  }

  canReopenMonth(): boolean {
    // Only Admin can reopen month, and only if month is closed
    return this.authService.hasAdminAccess() && this.isMonthClosed;
  }

  canEditPreviousMonth(): boolean {
    // Only Admin can edit previous months
    return this.authService.hasAdminAccess();
  }

  closeMonth() {
    const monthName = this.getMonthName(this.selectedMonth);
    this.confirmationService.confirm({
      message: `Are you sure you want to close ${monthName} ${this.selectedYear}? After closing, no further edits will be allowed for this month.`,
      header: 'Confirm Close Month',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const request = {
          month: this.selectedMonth,
          year: this.selectedYear
        };

        this.allocationService.closeMonth(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `${monthName} ${this.selectedYear} has been closed successfully`,
              life: 1000
            });
            // Immediately update the state to show reopen button
            this.isMonthClosed = true;
            // Refresh data (checkMonthStatus is called in loadData)
            this.loadData();
          },
          error: (error) => {
            console.error('Error closing month:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to close month'
            });
          }
        });
      }
    });
  }

  reopenMonth() {
    const monthName = this.getMonthName(this.selectedMonth);
    this.confirmationService.confirm({
      message: `Are you sure you want to reopen ${monthName} ${this.selectedYear}? After reopening, edits will be allowed for this month.`,
      header: 'Confirm Reopen Month',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const request = {
          month: this.selectedMonth,
          year: this.selectedYear
        };

        this.allocationService.reopenMonth(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `${monthName} ${this.selectedYear} has been reopened successfully`,
              life: 1000
            });
            this.isMonthClosed = false;
            this.loadData();
          },
          error: (error) => {
            console.error('Error reopening month:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to reopen month'
            });
          }
        });
      }
    });
  }

  private isCurrentMonth(month: Month, year: number): boolean {
    const today = new Date();
    const currentMonth = (today.getMonth() + 1) as Month;
    const currentYear = today.getFullYear();

    return month === currentMonth && year === currentYear;
  }

  showUnallocatedEmployeesDialog(): void {
    this.displayUnallocatedDialog = true;
    // Always open on the actionable list; the other tabs are reference, not a to-do.
    this.workforceTab = 'unallocated';
    this.calculateUnallocatedEmployees();
  }

  hideUnallocatedDialog(): void {
    this.displayUnallocatedDialog = false;
  }

  calculateUnallocatedEmployees(): void {
    this.loadingUnallocated = true;

    try {
      // Get all employees that should be included in allocation
      // (active and not excluded from allocation)
      const eligibleEmployees = this.employees.filter(
        e => e.isActive === true && !(e.excludeFromAllocation === true)
      );

      // Week totals drive this rather than "has an allocation row", so the list agrees with
      // the Not Allocated card: someone can hold a row that is 0% in every week, which is
      // planned-for-nothing and belongs here just as much as someone with no row at all.
      const weekTotals = this.getEmployeeWeekTotals();

      this.unallocatedEmployees = eligibleEmployees.filter(employee => {
        const weeks = weekTotals.get(employee.id) ?? [0, 0, 0, 0, 0];
        return weeks.every(v => Math.round(v) === 0);
      });

      // Excluded: active staff deliberately kept out of allocation (shared services, etc).
      // They are not a gap to fill, so they are listed apart from the unallocated.
      this.excludedEmployees = this.employees.filter(
        e => e.isActive === true && e.excludeFromAllocation === true
      );

      // Inactive: leavers relevant to the month being viewed — either they had left by its
      // end, or they still hold an allocation in it. The second case matters: someone who
      // left in October still has July plan hours, and those hours are counted by the
      // Effort Variance report, so hiding them here would leave no screen that explains the
      // difference between the two totals.
      this.inactiveEmployees = this.employees
        .filter(e => e.isActive === false)
        .filter(e => this.leftOnOrBeforeSelectedPeriod(e) || this.hasAllocationInPeriod(e.id))
        .sort((a, b) => (b.inactiveDate ?? '').localeCompare(a.inactiveDate ?? ''));
    } catch (error) {
      console.error('Error calculating workforce dialog lists:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to calculate un-allocated employees'
      });
    } finally {
      this.loadingUnallocated = false;
    }
  }

  /**
   * True when the employee's last working day is on or before the end of the selected month.
   * Someone deactivated without a recorded date is included, because the alternative is
   * hiding a leaver entirely — the row shows "—" so the missing date stays visible.
   */
  private leftOnOrBeforeSelectedPeriod(employee: Employee): boolean {
    if (!employee.inactiveDate) {
      return true;
    }
    // inactiveDate is 'YYYY-MM-DD'; compare as text against the month's last day to avoid
    // timezone shifts turning the 1st or the 31st into the neighbouring month.
    const lastDay = new Date(this.selectedYear, this.selectedMonth, 0).getDate();
    const endOfMonth =
      `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return employee.inactiveDate.slice(0, 10) <= endOfMonth;
  }

  /**
   * True when the employee holds any allocation row in the selected period, whatever the
   * percentages. Off-roster people (inactive or excluded) who answer true are the reason
   * the Effort Variance report counts more employees than this page's "allocated" figure:
   * that report includes anyone with a plan or a work log, with no active filter.
   */
  hasAllocationInPeriod(employeeId: string): boolean {
    return this.allocations.some(
      a => a.employeeId === employeeId
        && a.year === this.selectedYear
        && a.month === this.selectedMonth
    );
  }

  /**
   * People who hold a plan for this month but are not on the active roster — they have left
   * or are excluded from allocation. They are the difference between this page's "allocated"
   * figure and the Effort Variance report's employee count, which applies no active filter.
   */
  get offRosterAllocatedCount(): number {
    return [...this.excludedEmployees, ...this.inactiveEmployees]
      .filter(e => this.hasAllocationInPeriod(e.id)).length;
  }

  /** Spells out what that figure means — the term is not used anywhere else in the app. */
  get offRosterTooltip(): string {
    const n = this.offRosterAllocatedCount;
    const people = n === 1 ? 'person has' : 'people have';
    const total = this.allocatedEmployeeCount + n;
    return `${n} ${people} an allocation for this month but have left or are excluded from `
      + `allocation, so they are not part of the ${this.totalEmployeesCount} above. `
      + `Reports that cover everyone — such as the Effort Variance report — count ${total}.`;
  }

  /** Rows for the tab currently open — drives both the table and the Excel export. */
  get workforceDialogRows(): Employee[] {
    switch (this.workforceTab) {
      case 'excluded': return this.excludedEmployees;
      case 'inactive': return this.inactiveEmployees;
      default: return this.unallocatedEmployees;
    }
  }

  selectWorkforceTab(tab: 'unallocated' | 'excluded' | 'inactive'): void {
    this.workforceTab = tab;
  }

  /** Identity track-by: the tab name IS the key, so the table rebuilds when it changes. */
  trackByTabKey(_index: number, key: string): string {
    return key;
  }

  /** Human label for the open tab — used in the export file name and its toasts. */
  get workforceTabLabel(): string {
    switch (this.workforceTab) {
      case 'excluded': return 'Excluded';
      case 'inactive': return 'Inactive';
      default: return 'Un-Allocated';
    }
  }

  /** One line under the tabs saying what the list in view actually is. */
  get workforceTabHint(): string {
    switch (this.workforceTab) {
      case 'excluded':
        return 'Active employees set to be excluded from allocation, so they are not counted as a gap.';
      case 'inactive':
        return 'Employees who have left, plus any who still hold a plan for this month.';
      default:
        return 'Active and allocatable, but planned for 0% in every week of this month.';
    }
  }

  /** Empty-state wording per tab — "all allocated" is only true of the first list. */
  get workforceTabEmptyMessage(): string {
    switch (this.workforceTab) {
      case 'excluded': return 'No employees are excluded from allocation.';
      case 'inactive': return 'No employees have left on or before this month.';
      default: return 'All eligible employees are allocated for this month.';
    }
  }

  /**
   * Employees holding any planned time this month — the complement of the Not Allocated tab
   * within the active roster. The dialog's tabs each show people who are NOT on the plan, so
   * without this the count that matters most has to be worked out from the cards behind it.
   */
  get allocatedEmployeeCount(): number {
    return this.totalEmployeesCount - this.notAllocatedCount;
  }

  /** Formats an inactiveDate for display; '—' when the leaver has no recorded date. */
  formatInactiveDate(value: string | undefined): string {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getServiceGroupName(serviceGroupId: string | undefined): string {
    if (!serviceGroupId) return '-';
    const sg = this.serviceGroups.find(s => s.id === serviceGroupId);
    return sg?.name ?? '-';
  }

  /** Resolve cost center display name from id when API returns null (e.g. after DB restore with missing FK). */
  getCostCenterName(costCenterId: string | undefined): string {
    if (!costCenterId) return '-';
    const cc = this.costCenters.find(c => c.id === costCenterId);
    return cc?.name ?? '-';
  }

  /**
   * Allocation rows often don't persist cost center on the allocation record (it's owned by Project).
   * So for table display, fall back to the project's cost center when allocation cost center is empty.
   */
  getAllocationCostCenterName(allocation: Allocation): string {
    const direct = allocation.costCenterName?.trim();
    if (direct) return direct;

    if (allocation.costCenterId) {
      const fromList = this.getCostCenterName(allocation.costCenterId);
      if (fromList && fromList !== '-') return fromList;
    }

    const project = this.projects.find(p => p.id === allocation.projectId);
    const projectName = project?.costCenterName?.trim();
    if (projectName) return projectName;
    return this.getCostCenterName(project?.costCenterId);
  }

  /**
   * Cost center name for the Excel export.
   *
   * Uses the same resolution the grid renders with, so the file matches the screen, then
   * strips the "(Presales)" badge: that suffix is a UI affordance for telling a pursuit apart
   * at a glance, not part of the cost center's name, and Finance groups on this column.
   * The Presales Yes/No column already carries that fact in a form Excel can pivot on.
   */
  private resolveExportCostCenterName(allocation: Allocation): string {
    // Resolve by ID against the live cost-center list FIRST, exactly as the Edit Allocation
    // dropdown does. getAllocationCostCenterName() prefers allocation.costCenterName, which is
    // a snapshot written when the row was saved — for presales rows whose cost center has since
    // been corrected, that snapshot still holds the superseded name, so the form showed the
    // right value while the export reported the old one.
    //
    // Falls back to the shared resolver (project's cost center, then the stored copy) for rows
    // with no costCenterId of their own.
    let resolved = '';

    if (allocation.costCenterId) {
      const fromList = this.getCostCenterName(allocation.costCenterId);
      if (fromList && fromList !== '-') resolved = fromList;
    }

    if (!resolved) {
      resolved = this.getAllocationCostCenterName(allocation);
    }

    if (!resolved || resolved === '-') return '';

    // Strip the "(Presales)" badge: it is a UI affordance for spotting a pursuit at a glance,
    // not part of the cost center's name, and Finance groups on this column. The Presales
    // Yes/No column already carries that fact in a form Excel can pivot on.
    return resolved.replace(/\s*\(Presales\)\s*$/i, '').trim();
  }

  /** Close the unallocated dialog and open the Add Allocation dialog with the given employee pre-selected. */
  openAddAllocationForEmployee(employee: Employee): void {
    this.hideUnallocatedDialog();
    setTimeout(() => {
      if (!this.canAddAllocation()) {
        this.messageService.add({ severity: 'warn', summary: 'Month Closed', detail: 'This month is closed. Only Admin users can add allocations.', life: 3000 });
        return;
      }
      this.selectedAllocation = this.getEmptyAllocation();
      this.selectedAllocation.employeeId = employee.id;
      this.fieldTouched = {};
      this.isEditMode = false;
      this.onEmployeeChange(employee.id);
      this.displayDialog = true;
    }, 150);
  }

  // Row quick-add: open the Add dialog pre-filled with this row's employee (respects month-closed guard).
  quickAddForRow(allocation: Allocation, event?: Event): void {
    event?.stopPropagation();
    if (!this.canAddAllocation()) {
      this.messageService.add({ severity: 'warn', summary: 'Month Closed', detail: 'This month is closed. Only Admin users can add allocations.', life: 3000 });
      return;
    }
    this.selectedAllocation = this.getEmptyAllocation();
    this.selectedAllocation.employeeId = allocation.employeeId;
    this.fieldTouched = {};
    this.isEditMode = false;
    this.onEmployeeChange(allocation.employeeId);
    this.displayDialog = true;
  }

  exportUnallocatedToExcel(): void {
    const rows = this.workforceDialogRows;
    const label = this.workforceTabLabel;

    if (!rows || rows.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: `No ${label.toLowerCase()} employees to export`
      });
      return;
    }

    try {
      const isInactive = this.workforceTab === 'inactive';
      const isOffRoster = this.workforceTab !== 'unallocated';
      const monthName = this.getMonthName(this.selectedMonth);

      // Every row carries the same keys. Building them conditionally per row would let the
      // first row decide the sheet's columns, and json_to_sheet/autoFitColumns both read the
      // header order from it — a later row's extra key would then be dropped.
      const worksheetData = rows.map(emp => {
        const row: Record<string, unknown> = {
          'Employee Name': emp.name,
          'Email': emp.email || '',
          'Role': this.getRoleName(emp.roleId),
          'Designation': emp.designationName || '',
          'Category': emp.categoryName || '',
          'Service Group': emp.serviceGroupName || this.getServiceGroupName(emp.serviceGroupId)
        };

        if (isInactive) {
          row['Last Working Day'] = this.formatInactiveDate(emp.inactiveDate);
        }

        if (isOffRoster) {
          // Named for the month it describes, so the column stands alone once the sheet is
          // filtered, sorted or pasted somewhere else.
          row[`Allocated in ${monthName} ${this.selectedYear}`] =
            this.hasAllocationInPeriod(emp.id) ? 'Yes' : 'No';
        }

        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      // Without this the headers are clipped by the default column width — "Allocated in
      // July 2026" in particular runs under the next column.
      autoFitColumns(worksheet, worksheetData);
      addAutoFilter(worksheet);

      const workbook = XLSX.utils.book_new();
      // Excel caps sheet names at 31 characters, so the period alone goes in the tab name
      // and the full label lives in the file name.
      XLSX.utils.book_append_sheet(workbook, worksheet, `${monthName} ${this.selectedYear}`);

      const slug = label.replace(/\s+/g, '_');
      const fileName = `${slug}_${this.selectedYear}_${String(this.selectedMonth).padStart(2, '0')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `${label} employees exported successfully`
      });
    } catch (error) {
      console.error('Error exporting workforce list:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export employees'
      });
    }
  }

}

